import { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Check, Pencil, Plus, Tags, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminApi } from '@/features/admin/services/adminApi';
import { useFeedStore } from '@/app/store/feedStore';
import { getErrorMessage, getErrorStatus } from '@/shared/services/api';
import { AdminTag } from '@/shared/types/api';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { TagChip } from '@/shared/components/TagChip';
import { DEFAULT_TAG_COLOR, TAG_COLOR_PRESETS, isHexColor, readableTextColor } from '@/shared/lib/tagColors';
import { cn } from '@/shared/utils/cn';

interface FormState {
  name: string;
  description: string;
  color: string;
  isActive: boolean;
}

const emptyForm: FormState = { name: '', description: '', color: DEFAULT_TAG_COLOR, isActive: true };

const itemsLabel = (count: number) => `${count} item${count === 1 ? '' : 's'}`;

/**
 * True for the 409 (`reason: "stale"`) the API returns when a tag changed after this page read it
 * (another admin's edit, removal or reorder): an edit based on the outdated read, or a removal or
 * reorder that lost the race. A duplicate-name 409 has no `reason` and is not stale.
 */
const isStaleTagConflict = (error: unknown): boolean => {
  if (getErrorStatus(error) !== 409) return false;
  const data: unknown = (error as { response?: { data?: unknown } }).response?.data;
  return typeof data === 'object' && data !== null && (data as { reason?: unknown }).reason === 'stale';
};

/**
 * Admin management of the tag vocabulary (SC-376). Tags used to be a hardcoded list; now an admin
 * adds, edits, renames, recolours, reorders and removes them here. Tags are assigned by users only
 * (the classifier does not tag), so the description is optional guidance. Removing is a soft
 * delete: the tag stops being offered, while items that carry it keep it as a legacy chip.
 */
export function AdminTags() {
  const [tags, setTags] = useState<AdminTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<AdminTag | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);

  const load = async () => {
    try {
      setLoading(true);
      setTags(await adminApi.getTags());
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load tags'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Re-reads the tags without the full-page spinner, for after a change that bumped their versions.
  const refresh = async () => {
    try {
      setTags(await adminApi.getTags());
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load tags'));
    }
  };

  // The pickers, filters and chips elsewhere read the vocabulary from the feed store; mark it
  // stale so they reload it and show the change straight away (SC-376 AC 1-3).
  const vocabularyChanged = () => useFeedStore.getState().invalidateTagOptions();

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (tag: AdminTag) => {
    setEditing(tag);
    setForm({ name: tag.name, description: tag.description, color: tag.color, isActive: tag.isActive });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const onSave = async () => {
    const input = { name: form.name.trim(), description: form.description.trim(), color: form.color.trim() };
    if (!input.name) {
      toast.error('Name is required');
      return;
    }
    if (!isHexColor(input.color)) {
      toast.error('Colour must be a hex value like #1A2B3C');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await adminApi.updateTag(editing.id, {
          ...input,
          isActive: form.isActive,
          // The version this form was opened on: a save based on an outdated read is refused
          // rather than undoing another admin's change (e.g. renaming the tag back).
          expectedUpdatedAt: editing.updatedAt ?? editing.createdAt ?? null,
        });
        toast.success(editing.name !== input.name ? `Tag renamed to "${input.name}"` : 'Tag updated');
      } else {
        await adminApi.createTag(input);
        toast.success('Tag added');
      }
      vocabularyChanged();
      closeModal();
      await load();
    } catch (error) {
      if (editing && isStaleTagConflict(error)) {
        // Someone else changed the tag meanwhile: show the latest version instead of keeping a
        // form that would overwrite it. The vocabulary may have changed too.
        toast.error(getErrorMessage(error, 'This tag was changed by someone else. The latest version has been loaded.'));
        vocabularyChanged();
        closeModal();
        await load();
        return;
      }
      toast.error(getErrorMessage(error, 'Failed to save tag'));
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async (tag: AdminTag) => {
    const count = tag.itemCount ?? 0;
    const usage =
      count > 0
        ? `It is on ${itemsLabel(count)}; they keep it as a read-only legacy tag.`
        : 'No items carry it.';
    if (!window.confirm(`Remove tag "${tag.name}"? It will no longer be offered in pickers and filters. ${usage}`)) {
      return;
    }
    setBusy(true);
    try {
      await adminApi.deleteTag(tag.id);
      vocabularyChanged();
      toast.success('Tag removed');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove tag'));
      // 409: the tag changed meanwhile (another admin's edit or reorder); show the latest state.
      if (isStaleTagConflict(error)) {
        vocabularyChanged();
        await refresh();
      }
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= tags.length) return;

    const reordered = [...tags];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    const previous = tags;
    // Optimistic: the row moves at once. The arrows stay disabled (busy) until the save settles,
    // so a second move can't be built on an order the server has not stored yet.
    setTags(reordered);
    setBusy(true);
    try {
      await adminApi.reorderTags(reordered.map((t) => t.id));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reorder tags'));
      // 409: a tag changed meanwhile, so the order was not saved; show what the server holds.
      if (isStaleTagConflict(error)) {
        vocabularyChanged();
        await refresh();
      } else {
        setTags(previous);
      }
      setBusy(false);
      return;
    }
    vocabularyChanged();
    // The reorder bumped the moved tags' updatedAt. Re-read them (still busy, so no edit can start
    // meanwhile) so the next edit sends the current version instead of earning a stale 409.
    await refresh();
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  const activeCount = tags.filter((t) => t.isActive).length;
  const renaming = editing !== null && form.name.trim() !== '' && form.name.trim() !== editing.name;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {activeCount} active tag{activeCount === 1 ? '' : 's'}
          {tags.length > activeCount && `, ${tags.length - activeCount} removed`}
        </p>
        <button onClick={openCreate} className="btn-primary gap-2">
          <Plus className="h-4 w-4" />
          Add tag
        </button>
      </div>

      {tags.length === 0 ? (
        <div className="card">
          <EmptyState icon={Tags} title="No tags" description="Add the tags items can be categorised with." />
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-left">
              <thead>
                <tr className="bg-muted/50">
                  <th className="w-20 px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Order</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Tag</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">Items</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {tags.map((tag, index) => (
                  <tr key={tag.id} className={cn('hover:bg-muted/50', !tag.isActive && 'opacity-70')}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => move(index, -1)}
                          disabled={busy || index === 0}
                          className="btn-icon btn-ghost h-7 w-7"
                          aria-label={`Move ${tag.name} up`}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => move(index, 1)}
                          disabled={busy || index === tags.length - 1}
                          className="btn-icon btn-ghost h-7 w-7"
                          aria-label={`Move ${tag.name} down`}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <TagChip name={tag.name} color={tag.color} legacy={!tag.isActive} title={tag.color} />
                      <div className="mt-1 max-w-md text-sm text-muted-foreground" title={tag.description}>
                        {tag.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-foreground">{tag.itemCount ?? 0}</td>
                    <td className="px-4 py-3">
                      <span className={cn(tag.isActive ? 'badge-success' : 'badge-neutral')}>
                        {tag.isActive ? 'Active' : 'Removed'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(tag)}
                          disabled={busy}
                          className="btn-icon btn-ghost h-8 w-8"
                          title="Edit tag"
                          aria-label={`Edit tag ${tag.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {tag.isActive && (
                          <button
                            onClick={() => onRemove(tag)}
                            disabled={busy}
                            className="btn-icon btn-ghost h-8 w-8 text-danger hover:text-danger"
                            title="Remove tag"
                            aria-label={`Remove tag ${tag.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs animate-fade-in">
          <div
            className="surface-overlay max-h-[90vh] w-full max-w-lg animate-slide-up overflow-y-auto p-6 shadow-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tag-dialog-title"
          >
            <h2 id="tag-dialog-title" className="mb-4 text-xl font-bold text-foreground">
              {editing ? 'Edit tag' : 'Add tag'}
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="tag-name" className="label">Name *</label>
                <input
                  id="tag-name"
                  className="input mt-1.5"
                  value={form.name}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Screening"
                />
                {renaming && (
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    Renaming updates the {itemsLabel(editing.itemCount ?? 0)} that carry this tag.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="tag-description" className="label">Description</label>
                <textarea
                  id="tag-description"
                  className="input mt-1.5 resize-none"
                  rows={2}
                  maxLength={500}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional - when this tag applies"
                />
              </div>
              <div>
                <span className="label" id="tag-colour-label">Colour</span>
                <div className="mt-1.5 flex flex-wrap gap-1.5" role="radiogroup" aria-labelledby="tag-colour-label">
                  {TAG_COLOR_PRESETS.map((preset) => {
                    const selected = form.color.toUpperCase() === preset.value;
                    return (
                      <button
                        key={preset.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        aria-label={preset.name}
                        title={`${preset.name} ${preset.value}`}
                        onClick={() => setForm({ ...form, color: preset.value })}
                        className={cn(
                          'flex h-7 w-7 items-center justify-center rounded-md border border-border transition-transform hover:scale-110',
                          selected && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                        )}
                        style={{ backgroundColor: preset.value, color: readableTextColor(preset.value) }}
                      >
                        {selected && <Check className="h-4 w-4" aria-hidden />}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Pick a custom colour"
                    value={isHexColor(form.color) ? form.color : DEFAULT_TAG_COLOR}
                    onChange={(e) => setForm({ ...form, color: e.target.value.toUpperCase() })}
                    className="h-8 w-10 cursor-pointer rounded-md border border-border bg-surface p-0.5"
                  />
                  <input
                    id="tag-color"
                    aria-label="Custom colour hex"
                    className={cn('input h-8 max-w-[8rem] font-mono text-sm', !isHexColor(form.color) && 'border-danger')}
                    value={form.color}
                    maxLength={7}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    placeholder="#1A2B3C"
                  />
                </div>
              </div>
              <div>
                <span className="label">Preview</span>
                <div className="mt-1.5" data-testid="tag-preview">
                  <TagChip
                    name={form.name.trim() || 'Tag name'}
                    color={isHexColor(form.color) ? form.color : null}
                  />
                </div>
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Active (offered in pickers and filters)
                </label>
              )}
            </div>
            <div className="mt-6 flex gap-2">
              <button type="button" onClick={closeModal} className="btn-outline flex-1">Cancel</button>
              <button type="button" onClick={onSave} disabled={saving} className="btn-primary flex-1">
                {saving && <Spinner className="text-primary-foreground" />}
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add tag'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
